import React, { useState } from 'react';
import {
    Alert,
    Button,
    Grid,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import CardActions from 'src/pages/sistema/components/CardActions';
import { OperacionLCTC } from '../types/types';
import { useLCTCCuentas, useLCTCCuentasHistoria } from '../hooks/useLCTCCuentas';
import { useLCTCLimiteTipos } from '../hooks/useLCTCCache';
import { useLoadOptions } from 'src/hooks/useLoadOptions';
import { EntityWithIdFields, QueryStatus } from 'src/types';
import OperacionLCTCCuentaFila from './OperacionLCTCCuentaCard';
import HistoryIcon from "@mui/icons-material/History";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { OperacionLCTCLimitesDialogHistorial } from "./OperacionLCTCLimitesDialogHistorial";
import { HmacSHA1 } from 'crypto-js';
import { OperacionLCTCCuentaVista, OperacionLCTCLimiteTipo } from '../types/dto';

interface SeccionLCTCLimitesTablaBodyProps {
    cuentas: OperacionLCTCCuentaVista[];
    status: QueryStatus;
    operacion: OperacionLCTC;
    loadLimitesOptions: () => Promise<OperacionLCTCLimiteTipo[]>;
    refetch: () => void;
}

const SeccionLCTCLimitesTablaBody = ({ cuentas, status, operacion, loadLimitesOptions, refetch }: SeccionLCTCLimitesTablaBodyProps) => {
    if (status === QueryStatus.Success) {
        return (
            <>
                {cuentas.map((cuenta) => (
                    <OperacionLCTCCuentaFila
                        key={cuenta.id}
                        cuenta={cuenta}
                        operacion={operacion}
                        loadLimitesOptions={loadLimitesOptions}
                        refetch={refetch}
                    />
                ))}
            </>
        );
    }

    if (status === QueryStatus.Loading) {
        return (
            <TableRow>
                <TableCell colSpan={5}>
                    <Skeleton variant={'rectangular'} height={'100px'} width={'100%'} />
                </TableCell>
            </TableRow>
        );
    }

    if (status === QueryStatus.Error || !cuentas) {
        return (
            <TableRow>
                <TableCell colSpan={5}>
                    <Alert severity="error">No se pudieron cargar los límites de la operación.</Alert>
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow>
            <TableCell colSpan={5}>
                <Skeleton variant={'rectangular'} height={'100px'} width={'100%'} />
            </TableCell>
        </TableRow>
    )

}

interface SeccionLCTCLimitesTablaProps {
    operacion: OperacionLCTC;
}

const SeccionLCTCLImitesTabla = ({ operacion }: SeccionLCTCLimitesTablaProps) => {
    const { cuentas, status, refetch } = useLCTCCuentas(operacion[EntityWithIdFields.Id]);
    const { historia, status: statusHistoria, refetch: refetchHistoria } = useLCTCCuentasHistoria(operacion[EntityWithIdFields.Id]);

    const [historialDialogOpen, setHistorialDialogOpen] = useState(false);

    const limitesTipos = useLCTCLimiteTipos();
    const loadLimitesOptions = useLoadOptions({
        data: limitesTipos.limiteTipos,
        loading: limitesTipos.isLoading,
        isError: limitesTipos.isError,
        error: limitesTipos.error,
    });

    if (status === QueryStatus.Loading) {
        return <Typography>Cargando límites...</Typography>;
    }

    if (status === QueryStatus.Error || !cuentas) {
        return <Alert severity="error">No se pudieron cargar los límites de la operación.</Alert>;
    }

    return (
        <>
            <CardActions title='Límites de la Operación' icon='mdi:currency-usd' colapsable>
                <Grid container alignItems="center" justifyContent="flex-end" sx={{ mb: 2 }}>
                    <Grid item>

                        <Button
                            size="small"
                            startIcon={<HistoryIcon />}
                            onClick={() => setHistorialDialogOpen(true)}
                            sx={{ textTransform: 'none' }}
                            variant={"contained"}
                            disabled={statusHistoria === QueryStatus.Loading}
                        >
                            Historial
                        </Button>
                    </Grid>
                </Grid>

                <TableContainer component={Paper}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ '& .MuiTypography-root': { fontSize: '0.7rem' } }}>
                                <TableCell><Typography variant="body2">Tipo Cliente</Typography></TableCell>
                                <TableCell><Typography variant="body2">Sub-tipo</Typography></TableCell>
                                <TableCell align="right"><Typography variant="body2">Límite de concertación (% del AUM)</Typography></TableCell>
                                {/* <TableCell align="right"><Typography variant="body2">Límite de liquidación
                                    DVP</Typography></TableCell>
                                <TableCell align="right"><Typography variant="body2">Límite de liquidación
                                    FOP</Typography></TableCell> */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <SeccionLCTCLimitesTablaBody
                                cuentas={cuentas}
                                status={status}
                                loadLimitesOptions={loadLimitesOptions}
                                operacion={operacion}
                                refetch={refetch}
                            />
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardActions>
            <OperacionLCTCLimitesDialogHistorial
                open={historialDialogOpen}
                onClose={() => setHistorialDialogOpen(false)}
                historia={historia}
            />
        </>
    );
}

export default SeccionLCTCLImitesTabla;
